import { AppShell } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { EventName, IdentityRole, isNoanError, NoanError } from "core";
import { usePostHog } from "posthog-js/react";
import { useEffect, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import {
  Navigate,
  Outlet,
  ScrollRestoration,
  useLocation,
} from "react-router-dom";
import { AuthContext } from "../../auth/AuthContext";
import { useOptionalAuthContext } from "../../auth/useAuthContext";
import { useIdentity } from "../../auth/useIdentity";
import { isDeployed } from "../../config";
import { LocalStorageKeys } from "../../utils/cacheKeys";
import { safeReturnTo, SearchParam } from "../../utils/searchParams";
import { useUpdateReferral } from "../../utils/useAccountMutation";
import {
  clearStoredTrackingParams,
  getStoredTrackingParams,
  getUserProperties,
  useTrackEvent,
} from "../../utils/useTrackEvent";
import { ZedIndex } from "../../utils/zedIndex";
import { FullPageLoader } from "../FullPageLoader";
import { UpgradePricingModal } from "../PricingModal/UpgradePricingModal";
import { BrandingAdapter } from "../ThemeProvider/BrandingAdapter";
import classes from "./Layout.module.css";
import {
  isClaimProjectPage,
  isGuestOnboardingPage,
  isOAuthConsentPage,
  isOnboardingPage,
  isResetPasswordPage,
  isSubscribePage,
  LayoutVariant,
  topHeaderHeights,
} from "./layoutConfig";
import { SidebarLayout } from "./SidebarLayout";

/**
 * Sends the user to the destination Login stashed before Google/magic link,
 * re-checked with safeReturnTo since localStorage isn't trusted.
 */
function RestoreReturnTo({ children }: React.PropsWithChildren) {
  const [stashed, setStashed] = useState(() =>
    localStorage.getItem(LocalStorageKeys.ReturnTo),
  );
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    localStorage.removeItem(LocalStorageKeys.ReturnTo);
  }, []);

  const destination = stashed != null ? safeReturnTo(stashed) : null;
  const arrived = destination === pathname + search + hash;

  useEffect(() => {
    if (arrived) setStashed(null);
  }, [arrived]);

  /**
   * Rendering the app before we get there would mount the landing route's
   * own redirects (/ goes to /activity) and they'd win.
   */
  if (destination != null && !arrived) {
    return <Navigate to={destination} replace />;
  }
  return children;
}

export function AuthedLayout({ variant }: { variant?: LayoutVariant }) {
  const posthog = usePostHog();
  const { pathname, search } = useLocation();
  const [isFirstSessionLogin, setIsFirstSessionLogin] = useLocalStorage({
    key: LocalStorageKeys.FirstSessionLogin,
    defaultValue: false,
    getInitialValueInEffect: false,
  });
  const [cookies, setCookie] = useCookies(["dub_id"]);

  const {
    user,
    signOut,
    isLoadingUser,
    authError,
    jwtBearerToken,
    mustSetPassword,
    hasThirdPartyProvider,
    mustCompletePostSignup,
  } = useOptionalAuthContext();
  const {
    identity,
    isActive,
    isCustomer,
    isPaying,
    isLoading: isLoadingIdentity,
    hasInsightsAccess,
    isError: identityError,
    mustCompleteOnboarding,
    hasDubId,
  } = useIdentity();
  const { updateReferral } = useUpdateReferral();
  const { track } = useTrackEvent();

  const isGuest = identity?.primaryRole === IdentityRole.Guest;
  const showGuestOnboarding =
    isGuest &&
    !isGuestOnboardingPage(pathname) &&
    !isResetPasswordPage(pathname);
  const isSubscriptionRequired =
    !isLoadingIdentity &&
    !isActive &&
    !isOnboardingPage(pathname) &&
    !isGuestOnboardingPage(pathname) &&
    !isResetPasswordPage(pathname) &&
    !isSubscribePage(pathname);

  useEffect(() => {
    /**
     * We're not tracking the event in the supabase.auth.onAuthStateChange in supabase.ts file directly mainly because
     * there are a couple of not initialized yet: react-query client, ...
     * The supabase SIGNED_IN event can be triggered multiple times within the same session (e.g. when refreshing a page).
     * To make sure we only send one logged_in user event per session, we store the very first time the event is being fired.
     */
    if (isFirstSessionLogin && user != null) {
      setIsFirstSessionLogin(false);
      track(EventName.LoggedIn);
    }
  }, [isFirstSessionLogin, setIsFirstSessionLogin, track, user]);

  /**
   * Once we have user information, update third-party tools:
   */
  useEffect(() => {
    if (isDeployed && user != null) {
      try {
        posthog.identify(user.email, {
          ...getUserProperties(user),
          ...getStoredTrackingParams(),
        });
        clearStoredTrackingParams();

        // Save the Dub click id from the cookies to the account to add it
        // later in the Stripe customer metadata for tracking conversions after checkout.
        const dubId = cookies["dub_id"];
        if (dubId && !isLoadingIdentity && !hasDubId) {
          updateReferral(
            { dubId },
            {
              onSuccess() {
                setCookie("dub_id", "", {
                  expires: new Date(0),
                });
              },
            },
          );
        }
      } catch (err) {
        console.warn("Error while identifying user");
        console.error(err);
      }
    }
  }, [
    cookies,
    hasDubId,
    isLoadingIdentity,
    posthog,
    setCookie,
    updateReferral,
    user,
  ]);

  useEffect(() => {
    if (identityError) {
      signOut();
    }
  }, [identityError, signOut]);

  if (mustCompletePostSignup && pathname !== "/join") {
    // User must complete post-signup tasks
    return <Navigate to="/join" />;
  } else if (authError != null) {
    const errorType =
      isNoanError(authError) && authError.errorType === NoanError.NotLoggedIn
        ? "no-auth"
        : "auth-error";

    // Unable to fetch auth for some reason - might be missing user.
    return <Navigate to={`/login?reason=${errorType}`} />;
  } else if (isLoadingUser || isLoadingIdentity) {
    // Is loading user or identity data, and there's no cached identity data available
    return <FullPageLoader />;
  } else if (
    mustCompleteOnboarding &&
    !isOnboardingPage(pathname) &&
    !isClaimProjectPage(pathname) &&
    !isOAuthConsentPage(pathname)
  ) {
    // Redirect to onboarding before subscription paywall (users can onboard without paying)
    return <Navigate to={"/welcome"} />;
  } else if (isSubscriptionRequired) {
    return <Navigate to={"/subscribe"} />;
  } else if (showGuestOnboarding) {
    return <Navigate to={"/guest/welcome"} />;
  } else if (
    (user == null && !isLoadingUser) ||
    (identity == null && !isLoadingIdentity)
  ) {
    return (
      <Navigate
        to={`/login?reason=no-auth&${SearchParam.ReturnTo}=${encodeURIComponent(pathname + search)}`}
      />
    );
  }

  return (
    <AuthContext.Provider
      value={{
        jwtBearerToken,
        user: user!,
        identity: identity!,
        isAccountActive: isActive,
        isCustomer,
        isPayingCustomer: isPaying,
        isLoadingIdentity,
        isLoadingUser,
        authError,
        mustSetPassword,
        hasThirdPartyProvider,
        mustCompletePostSignup,
        mustCompleteOnboarding,
        signOut,
        hasInsightsAccess,
      }}
    >
      <RestoreReturnTo>
        <BrandingAdapter>
          <Layout variant={variant} />
          <UpgradePricingModal />
        </BrandingAdapter>
      </RestoreReturnTo>
    </AuthContext.Provider>
  );
}

export interface LayoutProps {
  variant?: LayoutVariant;
  topHeader?: React.JSX.Element;
  withPadding?: boolean;
}

function DefaultLayout({ topHeader, withPadding }: LayoutProps) {
  const shellPadding = { base: "sm", md: "xl" };
  const mainPadding = {
    base: topHeader ? topHeaderHeights.mobile : 0,
    xs: topHeader ? topHeaderHeights.desktop : 0,
  };

  return (
    <AppShell
      p={withPadding ? shellPadding : undefined}
      bg="var(--app-background)"
    >
      <AppShell.Header zIndex={ZedIndex.Header} withBorder={false}>
        {topHeader}
      </AppShell.Header>

      <AppShell.Main pt={mainPadding}>
        <Outlet />
        <ScrollRestoration getKey={(location) => location.pathname} />
      </AppShell.Main>
    </AppShell>
  );
}

export function MinimalLayout() {
  return (
    <div className={classes.minimalRoot}>
      <Outlet />
    </div>
  );
}

export function Layout({ variant, topHeader, withPadding }: LayoutProps) {
  /**
   * Redirect beta.getnoan.com to app.getnoan.com in all cases, preserving
   * path information.
   */
  useEffect(() => {
    const { host, pathname, search } = document.location;

    if (host === "beta.getnoan.com") {
      const newLocation = new URL(`https://app.getnoan.com${pathname}`);
      newLocation.search = search;
      // document.location.assign(newLocation);
    }
  }, []);

  const content = useMemo(() => {
    if (variant === "sidebar") {
      return <SidebarLayout topHeader={topHeader} />;
    } else if (variant === "minimal") {
      return <MinimalLayout />;
    } else {
      return (
        <DefaultLayout
          withPadding={withPadding}
          variant={variant}
          topHeader={topHeader}
        />
      );
    }
  }, [topHeader, variant, withPadding]);

  return content;
}
